import React, { useMemo } from "react";

import { redactHostname } from "./format";

// Same diamond proportions as WeaveLoader, scaled down for hostname redaction.
const HALF_W = 9;
const HALF_H = 5;
const DIAMOND = `M${-HALF_W} 0L0 ${-HALF_H}L${HALF_W} 0L0 ${HALF_H}Z`;
// Brick lattice like the logo: same-row pitch almost touching, odds nest in between.
const COL = HALF_W * 2 + 1;
const ROW = HALF_H + 1;
const MAX_TILES = 13;
const STROKE = 1.2;

/**
 * Redacts a hostname with Weave diamond tiles. Keeps the TLD so shared cards
 * still read as domains without revealing the brand.
 */
const PrivacyMask = ({ hostname, className = "" }) => {
  const { tld, length } = redactHostname(hostname);
  const tileCount = Math.min(length || 8, MAX_TILES);

  const tiles = useMemo(
    () => Array.from({ length: tileCount }, (_, i) => {
      const row = i % 2;
      // Five tones; the darkest bucket becomes an outline instead of a heavy fill.
      const bucket = (i * 17) % 5;
      return {
        x: i * (COL / 2),
        y: row === 0 ? 0 : ROW,
        outline: bucket === 4,
        opacity: 0.28 + bucket * 0.07,
      };
    }),
    [tileCount],
  );

  // Pad for stroke so outlined tips aren't clipped; otherwise hug the diamonds.
  const pad = STROKE;
  const lastX = (tileCount - 1) * (COL / 2);
  const viewX = -HALF_W - pad;
  const viewY = -HALF_H - pad;
  const viewW = lastX + HALF_W * 2 + pad * 2;
  const viewH = HALF_H * 2 + ROW + pad * 2;

  return (
    <div
      className={`flex items-center text-sm font-medium text-theme-text ${className}`.trim()}
      aria-label="Hidden domain"
    >
      <svg
        width={viewW}
        height={viewH}
        viewBox={`${viewX} ${viewY} ${viewW} ${viewH}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        className="block shrink-0 text-theme-text"
      >
        {tiles.map((tile) => (
          <path
            key={tile.x}
            d={DIAMOND}
            transform={`translate(${tile.x} ${tile.y})`}
            fill={tile.outline ? "var(--theme-main-bg)" : "currentColor"}
            stroke={tile.outline ? "currentColor" : undefined}
            strokeWidth={tile.outline ? STROKE : undefined}
            opacity={tile.outline ? 0.45 : tile.opacity}
          />
        ))}
      </svg>
      <span className="opacity-55">{tld}</span>
    </div>
  );
};

export default PrivacyMask;
